import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ChevronRight } from "lucide-react";

interface DailyAyah {
  arabic: string;
  translation: string;
  surah: string;
  surahNum: number;
  ayah: number;
  theme: string;
}

const DAILY_POOL: DailyAyah[] = [
  {
    arabic: "إِنَّ مَعَ ٱلْعُسْرِ يُسْرًا",
    translation: "Indeed, with hardship comes ease.",
    surah: "Ash-Sharh", surahNum: 94, ayah: 6,
    theme: "Hope & Patience",
  },
  {
    arabic: "وَمَن يَتَوَكَّلْ عَلَى ٱللَّهِ فَهُوَ حَسْبُهُۥ",
    translation: "And whoever relies upon Allah — then He is sufficient for him.",
    surah: "At-Talaq", surahNum: 65, ayah: 3,
    theme: "Trust in Allah",
  },
  {
    arabic: "رَبِّ زِدْنِى عِلْمًا",
    translation: "My Lord, increase me in knowledge.",
    surah: "Ta-Ha", surahNum: 20, ayah: 114,
    theme: "Seeking Knowledge",
  },
  {
    arabic: "وَلَسَوْفَ يُعْطِيكَ رَبُّكَ فَتَرْضَىٰ",
    translation: "And your Lord is going to give you, and you will be satisfied.",
    surah: "Ad-Duha", surahNum: 93, ayah: 5,
    theme: "Divine Promise",
  },
  {
    arabic: "فَٱذْكُرُونِىٓ أَذْكُرْكُمْ",
    translation: "So remember Me; I will remember you.",
    surah: "Al-Baqarah", surahNum: 2, ayah: 152,
    theme: "Remembrance",
  },
  {
    arabic: "وَهُوَ مَعَكُمْ أَيْنَ مَا كُنتُمْ",
    translation: "And He is with you wherever you are.",
    surah: "Al-Hadid", surahNum: 57, ayah: 4,
    theme: "Divine Nearness",
  },
  {
    arabic: "لَا يُكَلِّفُ ٱللَّهُ نَفْسًا إِلَّا وُسْعَهَا",
    translation: "Allah does not burden a soul beyond that it can bear.",
    surah: "Al-Baqarah", surahNum: 2, ayah: 286,
    theme: "Mercy & Ease",
  },
];

/**
 * Returns a consistent ayah for the current day (rotates daily).
 */
function getDailyIndex(): number {
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000,
  );
  return dayOfYear % DAILY_POOL.length;
}

export default function DailyVerse() {
  const ayah = useMemo(() => DAILY_POOL[getDailyIndex()], []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="card-elevated overflow-hidden"
    >
      <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 p-6 md:p-8 relative">
        <div className="absolute inset-0 pattern-islamic opacity-10" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-emerald-300" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300">
              Verse of the Day — {ayah.theme}
            </span>
          </div>

          <p className="font-arabic text-2xl md:text-3xl text-white leading-relaxed text-right mb-4" dir="rtl">
            {ayah.arabic}
          </p>

          <p className="text-emerald-100 text-sm md:text-base leading-relaxed italic">
            "{ayah.translation}"
          </p>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-emerald-700/30">
            <span className="text-xs text-emerald-300/60">
              Surah {ayah.surah} ({ayah.surahNum}:{ayah.ayah})
            </span>
            <Link
              to={`/quran/${ayah.surahNum}`}
              className="text-xs text-emerald-300 hover:text-white flex items-center gap-1 transition-colors"
            >
              Read full surah <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
